import { FunctionComponent, useEffect, useState } from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { TableContainer, Table as TableMUI, Box, Typography, useMediaQuery } from '@material-ui/core';
import clsx from 'clsx';
import TableHeader from './TableHeader';
import TableSpinner from './TableSpinner';
import TableNotFound from './TableNotFound';
import ProductVendor from 'model/ProductVendor';
import { Product, SortFields as SortFieldsProducts, SortOrder as SortOrderProducts } from '@spike/product-model';
import { SortFields as SortFieldsVendors, SortOrder as SortOrderVendors } from 'model/ProductVendor';
import { SortFields as SortFieldsCategories, SortOrder as SortOrderCategories } from 'model/ProductCategory';
import ProductCategory from 'model/ProductCategory';
import { TableBody as TableBodyMUI } from '@material-ui/core';
import ProductRow from './ProductRow';
import VendorRow from './VendorRow';
import CategoryRow from './CategoryRow';
import { useDispatch } from 'react-redux';
import { deleteThunk, saveThunk } from 'actions/productCategories/ProductCategoriesActions';
import ConfirmDialog from 'components/Products/UI/Dialog';
import UnnassignProductsDialog from '../UI/WarningDialog';
import { Spinner } from 'components/UI';

interface Props {
    products?: Array<Product>;
    vendors?: Array<ProductVendor>;
    categories?: Array<ProductCategory>;
    selectedView: string;
    loading?: boolean;
    onAssign: (category: ProductCategory) => void;
    onEditProduct: (product: Product) => void;
    onEditVendor: (vendor: ProductVendor) => void;
    onEditCategory: (vendor: ProductCategory) => void;
    className?: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        tableContainer: {
            display: 'block',
            width: '100%',
            height: '100%',
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            backgroundColor: '#FFFFFF'
        },
        table: {
            'width': '100%',
            'height': 'auto',
            'tableLayout': 'fixed',
            '& .MuiTableBody-root': {
                height: 'auto'
            },
            '& .MuiTableRow-root': {
                height: 'auto'
            },
            '& .MuiTableCell-root': {
                height: 'auto'
            }
        },
        textDialog: {
            fontWeight: 400,
            [theme.breakpoints.down('md')]: {
                fontSize: '13px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '16px'
            }
        },
        mobileList: {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            width: '100%',
            height: '100%',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            background: '#FFFFFF',
            borderTop: '2px solid #000000'
        },
        mobileEmpty: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            width: '100%',
            padding: '50px 16px',
            textAlign: 'center'
        },
        mobileEmptyTitle: {
            fontSize: '1.5rem',
            fontWeight: 700,
            color: '#222222'
        },
        mobileEmptyDescription: {
            fontSize: '1rem',
            color: '#222222'
        },
        spinnerWrapper: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            minHeight: '200px',
            height: '100%',
            background: '#FFFFFF'
        }
    })
);

export const Table: FunctionComponent<Props> = props => {
    const classes = useStyles();
    const isMobile = useMediaQuery('(max-width:767.95px)');
    const isProductsMobile = props.selectedView === 'products' && isMobile;
    const isCategoriesMobile = props.selectedView === 'categories' && isMobile;
    const isVendorsMobile = props.selectedView === 'vendors' && isMobile;

    const dispatch = useDispatch();

    const [showDeleteCategoryDialog, setShowDeleteCategoryDialog] = useState(false);
    const [showActivateCategoryDialog, setShowActivateCategoryDialog] = useState(false);
    const [showDeactivateCategoryDialog, setShowDeactivateCategoryDialog] = useState(false);
    const [showUnnassignProducts, setShowUnnassignProducts] = useState(false);
    const [categoryToChange, setCategoryToChange] = useState<ProductCategory>();
    const [products, setProducts] = useState<Array<Product> | undefined>(props.products);
    const [vendors, setVendors] = useState<Array<ProductVendor> | undefined>(props.vendors);
    const [categories, setCategories] = useState<Array<ProductCategory> | undefined>(props.categories);

    const handleDelete = (category: ProductCategory) => {
        setCategoryToChange(category);
        if (category.products.length > 0) {
            setShowUnnassignProducts(true);
        } else {
            setShowDeleteCategoryDialog(true);
        }
    };

    const handleDeleteCategory = () => {
        dispatch(deleteThunk(categoryToChange!));
        setShowDeleteCategoryDialog(false);
    };

    const handleActivate = (category: ProductCategory) => {
        setCategoryToChange(category);
        setShowActivateCategoryDialog(true);
    };

    const handleActivateCategory = () => {
        if (categoryToChange) {
            const categoryAux = { ...categoryToChange };
            categoryAux.active = true;
            dispatch(saveThunk(categoryAux!));
            setShowActivateCategoryDialog(false);
        }
    };

    const handleDeactivate = (category: ProductCategory) => {
        setCategoryToChange(category);
        setShowDeactivateCategoryDialog(true);
    };

    const handleDeactivateCategory = () => {
        if (categoryToChange) {
            const categoryAux = { ...categoryToChange };
            categoryAux.active = false;
            dispatch(saveThunk(categoryAux!));
            setShowDeactivateCategoryDialog(false);
        }
    };

    useEffect(() => {
        setProducts(props.products);
        setVendors(props.vendors);
        setCategories(props.categories);
    }, [props.products, props.vendors, props.categories]);

    const sortProducts = (field: SortFieldsProducts | undefined, order: SortOrderProducts) => {
        if (field !== undefined) {
            if (props.products) {
                const productsAux = [...props.products];
                if (order !== SortOrderProducts.CREATION_DATE) {
                    productsAux.sort((product, otherProduct) => {
                        if (field === 'productName') {
                            if (product.name.toLowerCase() > otherProduct.name.toLowerCase()) {
                                return 1 * (order === SortOrderProducts.ASCENDANT ? 1 : -1);
                            } else if (product.name.toLowerCase() < otherProduct.name.toLowerCase()) {
                                return -1 * (order === SortOrderProducts.ASCENDANT ? 1 : -1);
                            } else {
                                return 0;
                            }
                        } else if (field === 'category') {
                            const productFormatted = product.category
                                ? product.category.name
                                    ? product.category.name.toLowerCase()
                                    : ''
                                : '';
                            const otherProductFormatted = otherProduct.category
                                ? otherProduct.category.name
                                    ? otherProduct.category.name.toLowerCase()
                                    : ''
                                : '';

                            if (productFormatted > otherProductFormatted) {
                                return 1 * (order === SortOrderProducts.ASCENDANT ? 1 : -1);
                            } else if (productFormatted < otherProductFormatted) {
                                return -1 * (order === SortOrderProducts.ASCENDANT ? 1 : -1);
                            } else {
                                return 0;
                            }
                        } else if (field === 'vendor') {
                            const vendorDefault = product.vendors ? product.vendors.find(v => v.default) : undefined;
                            const otherVendorDefault = otherProduct.vendors
                                ? otherProduct.vendors.find(v => v.default)
                                : undefined;
                            const productFormatted = vendorDefault
                                ? vendorDefault.vendor?.name
                                    ? vendorDefault.vendor.name.toLowerCase()
                                    : ''
                                : '';
                            const otherProductFormatted = otherVendorDefault
                                ? otherVendorDefault.vendor?.name
                                    ? otherVendorDefault.vendor?.name.toLowerCase()
                                    : ''
                                : '';

                            if (productFormatted > otherProductFormatted) {
                                return 1 * (order === SortOrderProducts.ASCENDANT ? 1 : -1);
                            } else if (productFormatted < otherProductFormatted) {
                                return -1 * (order === SortOrderProducts.ASCENDANT ? 1 : -1);
                            } else {
                                return 0;
                            }
                        } else if (field === 'brand') {
                            const productFormatted = product.brand ? product.brand.toLowerCase() : '';
                            const otherProductFormatted = otherProduct.brand ? otherProduct.brand.toLowerCase() : '';

                            if (productFormatted > otherProductFormatted) {
                                return 1 * (order === SortOrderProducts.ASCENDANT ? 1 : -1);
                            } else if (productFormatted < otherProductFormatted) {
                                return -1 * (order === SortOrderProducts.ASCENDANT ? 1 : -1);
                            } else {
                                return 0;
                            }
                        } else if (field === 'manufacturer') {
                            const productFormatted = product.brand ? product.brand.toLowerCase() : '';
                            const otherProductFormatted = otherProduct.brand ? otherProduct.brand.toLowerCase() : '';

                            if (productFormatted > otherProductFormatted) {
                                return 1 * (order === SortOrderProducts.ASCENDANT ? 1 : -1);
                            } else if (productFormatted < otherProductFormatted) {
                                return -1 * (order === SortOrderProducts.ASCENDANT ? 1 : -1);
                            } else {
                                return 0;
                            }
                        } else {
                            return 1;
                        }
                    });
                }
                setProducts(productsAux);
            }
        }
    };

    const sortVendors = (field: SortFieldsVendors | undefined, order: SortOrderVendors) => {
        if (field !== undefined) {
            if (props.vendors) {
                const vendorsAux = [...props.vendors.filter(v => !v.deleted)];
                if (order !== SortOrderVendors.CREATION_DATE) {
                    vendorsAux.sort((vendor, otherVendor) => {
                        if (field === 'vendorName') {
                            if (vendor.name.toLowerCase() > otherVendor.name.toLowerCase()) {
                                return 1 * (order === SortOrderVendors.ASCENDANT ? 1 : -1);
                            } else if (vendor.name.toLowerCase() < otherVendor.name.toLowerCase()) {
                                return -1 * (order === SortOrderVendors.ASCENDANT ? 1 : -1);
                            } else {
                                return 0;
                            }
                        } else if (field === 'contact') {
                            const vendorFormatted = vendor.contact ? vendor.contact.toLowerCase() : '';
                            const otherVendorFormatted = otherVendor.contact ? otherVendor.contact.toLowerCase() : '';

                            if (vendorFormatted > otherVendorFormatted) {
                                return 1 * (order === SortOrderVendors.ASCENDANT ? 1 : -1);
                            } else if (vendorFormatted < otherVendorFormatted) {
                                return -1 * (order === SortOrderVendors.ASCENDANT ? 1 : -1);
                            } else {
                                return 0;
                            }
                        } else if (field === 'phone') {
                            const vendorFormatted = vendor.phone ? vendor.phone.toLowerCase() : '';
                            const otherVendorFormatted = otherVendor.phone ? otherVendor.phone.toLowerCase() : '';

                            if (vendorFormatted > otherVendorFormatted) {
                                return 1 * (order === SortOrderVendors.ASCENDANT ? 1 : -1);
                            } else if (vendorFormatted < otherVendorFormatted) {
                                return -1 * (order === SortOrderVendors.ASCENDANT ? 1 : -1);
                            } else {
                                return 0;
                            }
                        } else if (field === 'email') {
                            const vendorFormatted = vendor.email ? vendor.email.toLowerCase() : '';
                            const otherVendorFormatted = otherVendor.email ? otherVendor.email.toLowerCase() : '';

                            if (vendorFormatted > otherVendorFormatted) {
                                return 1 * (order === SortOrderVendors.ASCENDANT ? 1 : -1);
                            } else if (vendorFormatted < otherVendorFormatted) {
                                return -1 * (order === SortOrderVendors.ASCENDANT ? 1 : -1);
                            } else {
                                return 0;
                            }
                        } else if (field === 'website') {
                            const vendorFormatted = vendor.website ? vendor.website.toLowerCase() : '';
                            const otherVendorFormatted = otherVendor.website ? otherVendor.website.toLowerCase() : '';

                            if (vendorFormatted > otherVendorFormatted) {
                                return 1 * (order === SortOrderVendors.ASCENDANT ? 1 : -1);
                            } else if (vendorFormatted < otherVendorFormatted) {
                                return -1 * (order === SortOrderVendors.ASCENDANT ? 1 : -1);
                            } else {
                                return 0;
                            }
                        } else if (field === 'state') {
                            const vendorFormatted = vendor.address
                                ? vendor.address.state
                                    ? vendor.address.state.toLowerCase()
                                    : ''
                                : '';
                            const otherVendorFormatted = otherVendor.address
                                ? otherVendor.address.state
                                    ? otherVendor.address.state.toLowerCase()
                                    : ''
                                : '';

                            if (vendorFormatted > otherVendorFormatted) {
                                return 1 * (order === SortOrderVendors.ASCENDANT ? 1 : -1);
                            } else if (vendorFormatted < otherVendorFormatted) {
                                return -1 * (order === SortOrderVendors.ASCENDANT ? 1 : -1);
                            } else {
                                return 0;
                            }
                        } else if (field === 'products') {
                            return (
                                (vendor.products.length - otherVendor.products.length) *
                                (order === SortOrderVendors.ASCENDANT ? 1 : -1)
                            );
                        } else if (field === 'status') {
                            if (vendor.active > otherVendor.active) {
                                return 1 * (order === SortOrderVendors.ASCENDANT ? 1 : -1);
                            } else if (vendor.active < otherVendor.active) {
                                return -1 * (order === SortOrderVendors.ASCENDANT ? 1 : -1);
                            } else {
                                return 0;
                            }
                        } else {
                            return 1;
                        }
                    });
                }
                setVendors(vendorsAux);
            }
        }
    };

    const sortCategories = (field: SortFieldsCategories | undefined, order: SortOrderCategories) => {
        if (field !== undefined) {
            if (props.categories) {
                const categoriesAux = [...props.categories];
                if (order !== SortOrderCategories.CREATION_DATE) {
                    categoriesAux.sort((category, otherCategory) => {
                        if (field === 'category') {
                            if (category.name.toLowerCase() > otherCategory.name.toLowerCase()) {
                                return 1 * (order === SortOrderVendors.ASCENDANT ? 1 : -1);
                            } else if (category.name.toLowerCase() < otherCategory.name.toLowerCase()) {
                                return -1 * (order === SortOrderVendors.ASCENDANT ? 1 : -1);
                            } else {
                                return 0;
                            }
                        } else if (field === 'products') {
                            return (
                                (category.products.length - otherCategory.products.length) *
                                (order === SortOrderVendors.ASCENDANT ? 1 : -1)
                            );
                        } else if (field === 'status') {
                            if (category.active > otherCategory.active) {
                                return 1 * (order === SortOrderVendors.ASCENDANT ? 1 : -1);
                            } else if (category.active < otherCategory.active) {
                                return -1 * (order === SortOrderVendors.ASCENDANT ? 1 : -1);
                            } else {
                                return 0;
                            }
                        } else {
                            return 1;
                        }
                    });
                }
                setCategories(categoriesAux);
            }
        }
    };

    const messageQuestionDelete = () => {
        return (
            <Box>
                <Typography className={classes.textDialog}>
                    Are you sure you want to delete <strong>{categoryToChange?.name}</strong> as a category?
                </Typography>
            </Box>
        );
    };

    const messageQuestionActivate = () => {
        return (
            <Box>
                <Typography className={classes.textDialog}>Are you sure you want to activate category?</Typography>
            </Box>
        );
    };

    const messageQuestionDeactivate = () => {
        return (
            <Box>
                <Typography className={classes.textDialog}>Are you sure you want to deactivate category?</Typography>
            </Box>
        );
    };

    const getBody = (): JSX.Element => {
        switch (props.selectedView) {
            case 'products': {
                return products && products.length > 0 ? (
                    <TableBodyMUI>
                        {products &&
                            products.map(p => {
                                return (
                                    <ProductRow
                                        key={p.uuid}
                                        product={p}
                                        onEdit={props.onEditProduct}
                                    />
                                );
                            })}
                    </TableBodyMUI>
                ) : (
                    <TableNotFound
                        title="Not Found!"
                        description="Products not found."
                    />
                );
            }
            case 'vendors': {
                let vendorsFiltered = [];
                if (vendors && vendors.length > 0) {
                    vendorsFiltered = vendors.filter(v => !v.deleted);
                }
                return vendorsFiltered.length > 0 ? (
                    <TableBodyMUI>
                        {vendors &&
                            vendors.map(v => {
                                if (!v.deleted) {
                                    return (
                                        <VendorRow
                                            key={v.uuid}
                                            vendor={v}
                                            onEdit={props.onEditVendor}
                                        />
                                    );
                                }
                            })}
                    </TableBodyMUI>
                ) : (
                    <TableNotFound
                        title="Not Found!"
                        description="Vendors not found."
                    />
                );
            }
            default: {
                return categories && categories.length > 0 ? (
                    <TableBodyMUI>
                        {categories &&
                            categories.map(c => {
                                return (
                                    <CategoryRow
                                        key={c.uuid}
                                        category={c}
                                        onRename={props.onEditCategory}
                                        onAssign={props.onAssign}
                                        onDelete={handleDelete}
                                        onActivate={handleActivate}
                                        onDeactivate={handleDeactivate}
                                    />
                                );
                            })}
                    </TableBodyMUI>
                ) : (
                    <TableNotFound
                        title="Not Found!"
                        description="Categories not found."
                    />
                );
            }
        }
    };

    const productsMobileList = (
        <div className={classes.mobileList}>
            {props.loading ? (
                <div className={classes.spinnerWrapper}>
                    <Spinner />
                </div>
            ) : products && products.length > 0 ? (
                products.map(p => (
                    <ProductRow
                        key={p.uuid}
                        product={p}
                        onEdit={props.onEditProduct}
                        mobile
                    />
                ))
            ) : (
                <div className={classes.mobileEmpty}>
                    <div className={classes.mobileEmptyTitle}>Not Found!</div>
                    <div className={classes.mobileEmptyDescription}>Products not found.</div>
                </div>
            )}
        </div>
    );

    const categoriesMobileList = (
        <div className={classes.mobileList}>
            {props.loading ? (
                <div className={classes.spinnerWrapper}>
                    <Spinner />
                </div>
            ) : categories && categories.length > 0 ? (
                categories.map(c => (
                    <CategoryRow
                        key={c.uuid}
                        category={c}
                        onRename={props.onEditCategory}
                        onAssign={props.onAssign}
                        onDelete={handleDelete}
                        onActivate={handleActivate}
                        onDeactivate={handleDeactivate}
                        mobile
                    />
                ))
            ) : (
                <div className={classes.mobileEmpty}>
                    <div className={classes.mobileEmptyTitle}>Not Found!</div>
                    <div className={classes.mobileEmptyDescription}>Categories not found.</div>
                </div>
            )}
        </div>
    );

    const vendorsMobileList = (
        <div className={classes.mobileList}>
            {props.loading ? (
                <div className={classes.spinnerWrapper}>
                    <Spinner />
                </div>
            ) : vendors && vendors.filter(v => !v.deleted).length > 0 ? (
                vendors
                    .filter(v => !v.deleted)
                    .map(v => (
                        <VendorRow
                            key={v.uuid}
                            vendor={v}
                            onEdit={props.onEditVendor}
                            mobile
                        />
                    ))
            ) : (
                <div className={classes.mobileEmpty}>
                    <div className={classes.mobileEmptyTitle}>Not Found!</div>
                    <div className={classes.mobileEmptyDescription}>Vendors not found.</div>
                </div>
            )}
        </div>
    );

    const dialogs = (
        <>
            <ConfirmDialog
                open={showDeleteCategoryDialog}
                title="Delete Category"
                centered
                question={messageQuestionDelete()}
                onCancel={() => setShowDeleteCategoryDialog(false)}
                onConfirm={handleDeleteCategory}
                confirmButtonLabel="Delete"
                cancelButtonLabel="Cancel"
            />
            <ConfirmDialog
                open={showActivateCategoryDialog}
                title="Activate Category"
                centered
                question={messageQuestionActivate()}
                onCancel={() => setShowActivateCategoryDialog(false)}
                onConfirm={handleActivateCategory}
                confirmButtonLabel="Activate"
                cancelButtonLabel="Cancel"
            />
            <ConfirmDialog
                open={showDeactivateCategoryDialog}
                title="Deactivate Category"
                centered
                question={messageQuestionDeactivate()}
                onCancel={() => setShowDeactivateCategoryDialog(false)}
                onConfirm={handleDeactivateCategory}
                confirmButtonLabel="Deactivate"
                cancelButtonLabel="Cancel"
            />
            <UnnassignProductsDialog
                open={showUnnassignProducts}
                title="Unable to delete"
                description="You must unnassign products from this category before deleting category."
                onOk={() => setShowUnnassignProducts(false)}
            />
        </>
    );

    if (isProductsMobile) {
        return (
            <>
                {productsMobileList}
                {dialogs}
            </>
        );
    }

    if (isVendorsMobile) {
        return (
            <>
                {vendorsMobileList}
                {dialogs}
            </>
        );
    }

    if (isCategoriesMobile) {
        return (
            <>
                {categoriesMobileList}
                {dialogs}
            </>
        );
    }

    return (
        <>
            <TableContainer className={clsx(classes.tableContainer, props.className)}>
                <TableMUI className={classes.table}>
                    <TableHeader
                        selectedView={props.selectedView}
                        onSortProducts={sortProducts}
                        onSortVendors={sortVendors}
                        onSortCategories={sortCategories}
                    />
                    {props.loading ? <TableSpinner /> : getBody()}
                </TableMUI>
            </TableContainer>
            {dialogs}
        </>
    );
};

export default Table;
